import { APP_ROUTES } from './auth';

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

const PROFILE_KEY = 'wakaana_profile';
const PROFILES_KEY = 'wakaana_profiles';
const DRIVER_KEY = 'wakaana_driver';
const ON_DUTY_KEY = 'wakaana_on_duty';
const PROFILE_PHONE_COOKIE = 'wakaana_profile_phone';
const DRIVER_ID_COOKIE = 'wakaana_driver_id';
const SESSION_COOKIE = 'wakaana_session';

function setCookie(name, value) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${60 * 60 * 24 * 30}; path=/; SameSite=Strict`;
}

function clearCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Strict`;
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split(';').find(c => c.trim().startsWith(`${name}=`));
  return match ? decodeURIComponent(match.trim().slice(name.length + 1)) : null;
}

export function getRouteForProfile(profile) {
  const role = String(profile?.role || '').toLowerCase();
  if (role === 'gestionnaire') return APP_ROUTES.gestionnaire;
  if (role === 'merchant') return APP_ROUTES.merchant;
  if (role === 'particulier') return APP_ROUTES.particulier;
  if (role === 'driver') return APP_ROUTES.driver;
  return null;
}

export function getStoredProfile() {
  const raw = localStorage.getItem(PROFILE_KEY);
  return raw ? safeParse(raw, null) : null;
}

export function setStoredProfile(profile) {
  if (!profile || typeof profile !== 'object') return;
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  if (profile.phone) setCookie(PROFILE_PHONE_COOKIE, profile.phone);
  else clearCookie(PROFILE_PHONE_COOKIE);
  // Store minimal session in a 30-day cookie to survive iOS ITP localStorage eviction
  const sessionSnap = {
    id: profile.id,
    role: profile.role,
    phone: profile.phone || null,
    name: profile.name || null,
    driver_id: profile.driver_id || null,
    account_status: profile.account_status || null,
  };
  setCookie(SESSION_COOKIE, JSON.stringify(sessionSnap));
}

export function getStoredSessionCookie() {
  const raw = getCookie(SESSION_COOKIE);
  return raw ? safeParse(raw, null) : null;
}

export function getStoredProfiles() {
  const raw = localStorage.getItem(PROFILES_KEY);
  const list = raw ? safeParse(raw, []) : [];
  return Array.isArray(list) ? list : [];
}

export function setStoredProfiles(profiles) {
  if (!Array.isArray(profiles)) return;
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

export function clearStoredProfileSession() {
  localStorage.removeItem(PROFILE_KEY);
  localStorage.removeItem(PROFILES_KEY);
  clearCookie(PROFILE_PHONE_COOKIE);
  clearCookie(SESSION_COOKIE);
}

export function getStoredDriver() {
  const raw = localStorage.getItem(DRIVER_KEY);
  return raw ? safeParse(raw, null) : null;
}

export function setStoredDriver(driver) {
  if (!driver || typeof driver !== 'object') return;
  localStorage.setItem(DRIVER_KEY, JSON.stringify(driver));
  if (driver.id != null) setCookie(DRIVER_ID_COOKIE, driver.id);
  else clearCookie(DRIVER_ID_COOKIE);
}

export function clearStoredDriverSession() {
  clearStoredProfileSession();
  localStorage.removeItem(DRIVER_KEY);
  localStorage.removeItem(ON_DUTY_KEY);
  clearCookie(DRIVER_ID_COOKIE);
}

export function getStoredOnDuty() {
  return localStorage.getItem(ON_DUTY_KEY) === 'true';
}

export function setStoredOnDuty(value) {
  if (value) localStorage.setItem(ON_DUTY_KEY, 'true');
  else localStorage.removeItem(ON_DUTY_KEY);
}

export function getStoredAppPath() {
  const activeRoute = getRouteForProfile(getStoredProfile());
  if (activeRoute) return activeRoute;

  if (getStoredDriver()) return APP_ROUTES.driver;

  const fallbackProfile = getStoredProfiles().find((profile) => getRouteForProfile(profile));
  return getRouteForProfile(fallbackProfile);
}

export function hasRoleAccess(role) {
  const wanted = String(role || '').toLowerCase();
  if (!wanted) return false;

  const active = getStoredProfile();
  if (String(active?.role || '').toLowerCase() === wanted) return true;

  return getStoredProfiles().some((p) => String(p?.role || '').toLowerCase() === wanted);
}

export function getProfileForRole(role) {
  const wanted = String(role || '').toLowerCase();
  if (!wanted) return null;

  const active = getStoredProfile();
  if (String(active?.role || '').toLowerCase() === wanted) return active;

  return getStoredProfiles().find((p) => String(p?.role || '').toLowerCase() === wanted) || null;
}
